// Pure weather/sea rules shared by El Tiempo, Playas, Montañas and the home hero.
// No React here, so each rule can be tested on its own (src/lib/__tests__/meteo.test.js).
import { madridDate, madridHour, madridParts, addDays, freshnessOf, parseTimestamp, hhmm } from './freshness';

const num = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// ─── Sky ────────────────────────────────────────────────────────────────────────────────────
// AEMET writes the beach sky in lower case ("nuboso", "muy nuboso") and the municipal one in
// sentence case ("Intervalos nubosos con lluvia escasa"). The old table had capitalised keys, so
// every beach fell through to 🌤️, rain included. Order matters: the more specific phrase first
// ("muy nuboso" before "nuboso", "poco nuboso" before "nuboso").
const SKY_RULES = [
  ['tormenta', '⛈️'],
  ['nieve', '🌨️'],
  ['aguanieve', '🌨️'],
  ['chubasco', '🌦️'],
  ['lluvia', '🌧️'],
  ['llovizna', '🌧️'],
  ['niebla', '🌫️'],
  ['bruma', '🌫️'],
  ['calima', '🌫️'],
  ['cubierto', '☁️'],
  ['muy nuboso', '☁️'],
  ['poco nuboso', '🌤️'],
  ['nubes altas', '🌤️'],
  ['intervalos nubosos', '⛅'],
  ['nuboso', '⛅'],
  ['despejado', '☀️'],
];

/** Emoji for an AEMET sky description, or null when we don't recognise it (never a guess). */
export function skyEmoji(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.toLowerCase().trim();
  for (const [key, emoji] of SKY_RULES) if (t.includes(key)) return emoji;
  return null;
}

// ─── Rain by period ─────────────────────────────────────────────────────────────────────────
// AEMET's municipal forecast gives rain probability per 6 h, 12 h and whole-day periods. The labels
// used to be shifted by one slot (00-06 «Noche», 12-18 «Mediodía», 18-24 «Tarde»): 12:00-18:00 is the
// afternoon and 18:00-24:00 the evening and night.
export const RAIN_PERIOD_LABELS = {
  '00-06': 'Madrugada', '06-12': 'Mañana', '12-18': 'Tarde', '18-24': 'Noche',
  '00-12': 'Mañana', '12-24': 'Tarde y noche', '00-24': 'Hoy',
};

/** The most detailed set of rain periods a day has (6 h, else 12 h, else the whole day), labelled. */
export function rainPeriods(list) {
  const all = (Array.isArray(list) ? list : []).filter(r => typeof r?.periodo === 'string' && r.periodo.includes('-'));
  const h6 = all.filter(r => ['00-06', '06-12', '12-18', '18-24'].includes(r.periodo));
  const h12 = all.filter(r => ['00-12', '12-24'].includes(r.periodo));
  const periods = h6.length >= 2 ? h6 : h12.length >= 2 ? h12 : all.filter(r => r.periodo === '00-24');
  return periods.map(r => ({ ...r, label: RAIN_PERIOD_LABELS[r.periodo] || r.periodo }));
}

// ─── Mountains ──────────────────────────────────────────────────────────────────────────────
/**
 * «606 m» (or «606 m de altitud» with `long`). An area whose figure is not the place's own height
 * says so: Fuente Dé's 1823 m is the upper cable-car station, «1823 m (estación superior del teleférico)».
 */
export function altitudeText(area, { long = false } = {}) {
  const alt = num(area?.altitude);
  if (alt == null) return '';
  const base = `${alt} m${long ? ' de altitud' : ''}`;
  return area.altitude_note ? `${base} (${area.altitude_note})` : base;
}

// ─── Beach forecast ─────────────────────────────────────────────────────────────────────────
const cleanDate = (f) => String(f || '').replace(/-/g, '').slice(0, 8);

/** The forecast entry for a Madrid date ('YYYY-MM-DD'), whether the API wrote ISO or YYYYMMDD. */
export function forecastFor(forecasts, dateStr) {
  if (!Array.isArray(forecasts) || !dateStr) return null;
  const want = dateStr.replace(/-/g, '');
  return forecasts.find(f => cleanDate(f?.fecha) === want) || null;
}

/** Today's entry; failing that the most recent past one (labelled as old by the page), never a
 *  future day passed off as today. */
export function todayOrLatest(forecasts, now = Date.now()) {
  if (!Array.isArray(forecasts) || !forecasts.length) return null;
  const today = madridDate(now).replace(/-/g, '');
  const exact = forecasts.find(f => cleanDate(f?.fecha) === today);
  if (exact) return exact;
  const past = forecasts.filter(f => cleanDate(f?.fecha) && cleanDate(f.fecha) < today)
    .sort((a, b) => cleanDate(b.fecha).localeCompare(cleanDate(a.fecha)));
  return past[0] || null;
}

export const AFTERNOON_FROM_HOUR = 14;

/**
 * Sky, wind and waves to show for a beach day right now. From 14:00 Madrid the afternoon values
 * are the ones that still lie ahead; before that, the morning's. AEMET's afternoon "fuerte" used
 * to stay hidden behind the morning "moderado" all day.
 */
export function beachConditions(fc, now = Date.now()) {
  if (!fc) return null;
  const hasAfternoon = fc.cielo_tarde != null || fc.viento_tarde != null || fc.oleaje_tarde != null;
  const afternoon = hasAfternoon && madridHour(now) >= AFTERNOON_FROM_HOUR;
  return {
    afternoon,
    cielo: afternoon ? (fc.cielo_tarde || fc.cielo) : fc.cielo,
    viento: afternoon ? (fc.viento_tarde || fc.viento) : fc.viento,
    oleaje: afternoon ? (fc.oleaje_tarde || fc.oleaje) : fc.oleaje,
  };
}

// AEMET's index: SUAVE (450) and CALOR AGRADABLE (460) are comfort; every other step is a level of
// discomfort, cold as well as hot. 410 muy frío · 420 frío · 430 muy fresco · 440 fresco ·
// 470 calor moderado · 480 calor fuerte.
export function sensacionTone(code, text) {
  const c = num(code);
  const t = (text || '').toLowerCase();
  const neutral = { key: 'confort', cls: 'text-slate-600 bg-slate-50' };
  if (c != null) {
    if (c >= 480) return { key: 'calor-fuerte', cls: 'text-red-700 bg-red-50' };
    if (c >= 470) return { key: 'calor', cls: 'text-orange-700 bg-orange-50' };
    if (c >= 450) return neutral;
    if (c >= 430) return { key: 'fresco', cls: 'text-sky-700 bg-sky-50' };
    return { key: 'frio', cls: 'text-blue-700 bg-blue-50' };
  }
  if (!t || t.includes('suave') || t.includes('agradable')) return neutral;
  if (t.includes('calor')) return t.includes('fuerte') || t.includes('mucho') ? { key: 'calor-fuerte', cls: 'text-red-700 bg-red-50' } : { key: 'calor', cls: 'text-orange-700 bg-orange-50' };
  if (t.includes('fresco')) return { key: 'fresco', cls: 'text-sky-700 bg-sky-50' };
  if (t.includes('frío') || t.includes('frio')) return { key: 'frio', cls: 'text-blue-700 bg-blue-50' };
  return neutral;
}

// ─── Tides ──────────────────────────────────────────────────────────────────────────────────
export function coefBand(c) {
  if (c < 50) return { key: 'muertas', label: 'Muertas', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
  if (c < 80) return { key: 'medias', label: 'Medias', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (c < 95) return { key: 'vivas', label: 'Vivas', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { key: 'muy-vivas', label: 'Muy vivas', cls: 'bg-red-50 text-red-700 border-red-200' };
}

/**
 * Today's coefficient(s). A day has one per high tide (91 in the morning, 95 in the afternoon);
 * the page used to show the first all day. `coeficiente` is the day's maximum and names the band;
 * null (the IHM backup, whose estimate was 25 points out) shows no coefficient at all.
 */
export function tideCoefficient({ coeficiente, coeficientes, coef_label } = {}) {
  const list = (Array.isArray(coeficientes) ? coeficientes : []).map(num).filter(v => v != null);
  const max = num(coeficiente) ?? (list.length ? Math.max(...list) : null);
  if (max == null) return null;
  const values = list.length ? list : [max];
  const band = coefBand(max);
  return { values, max, band, label: coef_label || band.label, text: values.join(' / ') };
}

/** Rising or falling, by Madrid clock, from a day's table of {hora 'H:MM', tipo}. */
export function tideTrend(tides, now = Date.now()) {
  if (!Array.isArray(tides) || !tides.length) return '';
  const { h, mi } = madridParts(now);
  const nowMins = h * 60 + mi;
  const sorted = tides
    .map(t => { const [hh, mm] = String(t.hora || '').split(':').map(Number); return { ...t, mins: (hh || 0) * 60 + (mm || 0) }; })
    .sort((a, b) => a.mins - b.mins);
  let prev = null, next = null;
  for (const t of sorted) {
    if (t.mins <= nowMins) prev = t;
    else if (!next) next = t;
  }
  // After a low tide the water rises; before the first high tide of the day it is rising too.
  if (prev) return prev.tipo === 'bajamar' ? 'subiendo' : 'bajando';
  if (next) return next.tipo === 'pleamar' ? 'subiendo' : 'bajando';
  return '';
}

// ─── Bathing water ──────────────────────────────────────────────────────────────────────────
// Single-sample reference values for coastal water (Directive 2006/7/EC, 95th percentile):
// excellent ≤250 E. coli / ≤100 enterococci, good ≤500 / ≤200. The directive grades a season of
// samples, not one, so a sample above the "good" values is flagged, not given a class of its own.
export const BATHING_REF = { ecoli: 500, enterococci: 200 };
export const WATER_UNITS = 'NMP/100 ml';

export function sampleQuality(ecoli, enterococci) {
  const e = num(ecoli), i = num(enterococci);
  if (e == null && i == null) return null;
  if ((e != null && e > BATHING_REF.ecoli) || (i != null && i > BATHING_REF.enterococci)) {
    return { key: 'over', label: 'Supera valor de referencia', cls: 'bg-red-50 text-red-700 border-red-200' };
  }
  if (e == null || i == null) return null;
  if (e <= 250 && i <= 100) return { key: 'excelente', label: 'Excelente', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  return { key: 'buena', label: 'Buena', cls: 'bg-torre-teal/10 text-torre-teal border-torre-teal/20' };
}

// ─── UV ─────────────────────────────────────────────────────────────────────────────────────
export const UV_NOW_MAX_AGE_S = 45 * 60;

/**
 * Which UV figure to show, and how to name it.
 *  - 06:00–21:00, with an Open-Meteo reading under 45 minutes old: «UV ahora».
 *  - Otherwise the day's forecast maximum for the day ahead: today until 21:00 and again after
 *    midnight (the old «UV máx mañana» at 02:00 meant the day after the coming one), tomorrow
 *    from 21:00. AEMET's maximum first (`aemetMaxFor(date)`), then Open-Meteo's, matched to the
 *    day it was computed on.
 * Returns null when there is nothing honest to show.
 */
export function pickUv({ now = Date.now(), realtime = null, receivedAt = null, aemetMaxFor = null } = {}) {
  const h = madridHour(now);
  const today = madridDate(now);
  const fr = freshnessOf(realtime?._freshness, { now, receivedAt, maxAgeS: UV_NOW_MAX_AGE_S, fallbackTime: realtime?.updated });
  const current = num(realtime?.current);
  if (h >= 6 && h < 21 && current != null && fr.fresh) {
    return { mode: 'now', value: Math.round(current), label: 'UV ahora', source: 'Open-Meteo', freshness: fr };
  }
  const target = h >= 21 ? addDays(today, 1) : today;
  const mode = target === today ? 'max_today' : 'max_tomorrow';
  const label = mode === 'max_today' ? 'UV máx hoy' : 'UV máx mañana';

  const aemet = num(aemetMaxFor ? aemetMaxFor(target) : null);
  if (aemet != null) return { mode, value: Math.round(aemet), label, source: 'AEMET', freshness: fr };

  if (realtime) {
    // The day Open-Meteo's "today" refers to is the day it was fetched.
    const t = fr.dataTime ?? (fr.ageS != null ? now - fr.ageS * 1000 : null);
    if (t != null) {
      const d = madridDate(t);
      const v = d === target ? num(realtime.max_today) : addDays(d, 1) === target ? num(realtime.max_tomorrow) : null;
      if (v != null) return { mode, value: Math.round(v), label, source: 'Open-Meteo', freshness: fr };
    }
  }
  return null;
}

export function uvBand(uv) {
  if (uv == null) return null;
  if (uv <= 2) return { label: 'Bajo', cls: 'text-emerald-700 bg-emerald-50' };
  if (uv <= 5) return { label: 'Moderado', cls: 'text-amber-700 bg-amber-50' };
  if (uv <= 7) return { label: 'Alto', cls: 'text-orange-700 bg-orange-50' };
  if (uv <= 10) return { label: 'Muy alto', cls: 'text-red-700 bg-red-50' };
  return { label: 'Extremo', cls: 'text-purple-700 bg-purple-50' };
}

// ─── Rivers ─────────────────────────────────────────────────────────────────────────────────
// SAIH Cantábrico's thresholds are called Seguimiento, Prealerta and Alerta (shown in yellow,
// orange and red). The backend's keys are the colours; people must only ever read the names.
export const RIVER_LEVELS = {
  normalidad: { key: 'normalidad', label: 'Normal', hex: '#10B981', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', rank: 0 },
  amarillo: { key: 'amarillo', label: 'Seguimiento', hex: '#F59E0B', cls: 'bg-amber-50 text-amber-800 border-amber-300', rank: 1 },
  naranja: { key: 'naranja', label: 'Prealerta', hex: '#F97316', cls: 'bg-orange-50 text-orange-800 border-orange-400', rank: 2 },
  rojo: { key: 'rojo', label: 'Alerta', hex: '#EF4444', cls: 'bg-red-50 text-red-800 border-red-400', rank: 3 },
  desconocido: { key: 'desconocido', label: 'Sin datos', hex: '#94A3B8', cls: 'bg-slate-100 text-slate-600 border-slate-200', rank: -1 },
};
const RIVER_ALIASES = {
  normal: 'normalidad', normalidad: 'normalidad', verde: 'normalidad',
  amarillo: 'amarillo', seguimiento: 'amarillo',
  naranja: 'naranja', prealerta: 'naranja',
  rojo: 'rojo', alerta: 'rojo',
};

/** The level for any spelling the API uses (colour key or level name); unknown → «Sin datos». */
export function riverLevel(severity) {
  const k = RIVER_ALIASES[String(severity || '').toLowerCase().trim()];
  return RIVER_LEVELS[k || 'desconocido'];
}

// "En vivo" only for river data under 30 minutes old (SAIH reports every 5).
export const RIVER_LIVE_MAX_S = 30 * 60;

// ─── Reservoirs ─────────────────────────────────────────────────────────────────────────────
/**
 * Fill percentage, computed from volume and capacity (SAIH's own figure disagreed with its own
 * hm³: 61,82 % next to 18,93/21,46). `porcentaje` is the server's version of the same sum; SAIH's
 * figure is kept to flag a disagreement of more than 2 points.
 */
export function embalsePct(emb) {
  if (!emb) return { pct: null, saih: null, differs: false };
  const vol = num(emb.volumen), cap = num(emb.capacidad);
  const computed = vol != null && cap != null && cap > 0 ? (vol / cap) * 100 : null;
  const ours = num(emb.porcentaje) ?? computed;
  const saih = num(emb.porcentaje_saih) ?? num(emb.porcentaje_llenado);
  const pct = ours ?? saih;
  const differs = ours != null && saih != null && Math.abs(ours - saih) > 2;
  return { pct, saih, differs };
}

// ─── AEMET warnings ─────────────────────────────────────────────────────────────────────────
const LEVEL_RANK = { red: 3, rojo: 3, orange: 2, naranja: 2, yellow: 1, amarillo: 1 };

/** Warnings worth showing: not green, not expired, one per event/level/zone, worst first. */
export function visibleAlerts(alerts, now = Date.now()) {
  if (!Array.isArray(alerts)) return [];
  const out = alerts.filter(a => {
    if (!a || !LEVEL_RANK[String(a.level || '').toLowerCase()]) return false;
    const exp = parseTimestamp(a.expires, now);
    return exp == null || exp > now;
  });
  const unique = out.filter((a, i, arr) => arr.findIndex(b => b.event === a.event && b.level === a.level && (b.zone || '') === (a.zone || '')) === i);
  const rank = (a) => LEVEL_RANK[String(a.level || '').toLowerCase()] || 0;
  return unique.sort((a, b) => rank(b) - rank(a)
    || (parseTimestamp(a.onset, now) ?? 0) - (parseTimestamp(b.onset, now) ?? 0));
}

const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];

/** «hoy a las 14:00», «mañana a las 08:00», «el sábado 27 sept a las 00:00» (Madrid). */
export function whenLabel(ms, now = Date.now()) {
  if (ms == null) return '';
  const d = madridDate(ms);
  const today = madridDate(now);
  const time = hhmm(ms);
  if (d === today) return `hoy a las ${time}`;
  if (d === addDays(today, 1)) return `mañana a las ${time}`;
  const [y, m, dd] = d.split('-').map(Number);
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, dd)).getUTCDay()];
  return `el ${wd} ${dd} ${MONTHS[m - 1]} a las ${time}`;
}

// ─── Observation ────────────────────────────────────────────────────────────────────────────
// Only for a server that sends no `_freshness` on the observation: judge its own timestamp by
// this. AEMET's hourly readings arrive up to about an hour late, so two hours is already old.
export const OBSERVATION_FALLBACK_MAX_AGE_S = 2 * 3600;

// ─── Pollen ─────────────────────────────────────────────────────────────────────────────────
const levelKey = (label) => String(label || '').toLowerCase().trim().replace(/\s+/g, '_');

/**
 * One pollen row: today's maximum (`today_level`, `today_peak`, `today_color`) when the server
 * sends it, the current hour (`label`, `value`) always, and tomorrow. A 0 forecast for tomorrow
 * is «Nulo», not the «?» the server used to send for it.
 */
export function pollenRow(p) {
  if (!p) return null;
  const hasToday = p.today_level != null && p.today_level !== '' && p.today_level !== '?';
  const nowLabel = p.label || null;
  const todayLabel = hasToday ? p.today_level : null;
  let tomorrow = p.tomorrow_level;
  if ((tomorrow == null || tomorrow === '?') && num(p.tomorrow_peak) === 0) tomorrow = 'Nulo';
  if (tomorrow === '?') tomorrow = null;
  const active = (num(p.value) || 0) > 0 || (num(p.today_peak) || 0) > 0;
  return {
    active,
    // The bar and the main label follow today's maximum when known, otherwise the current hour.
    mainLabel: todayLabel || nowLabel,
    mainColor: hasToday ? (p.today_color || p.color) : p.color,
    barKey: hasToday ? levelKey(todayLabel) : (p.level || levelKey(nowLabel)),
    hasToday,
    nowLabel,
    tomorrow: tomorrow && tomorrow !== (todayLabel || nowLabel) ? tomorrow : null,
    tomorrowColor: p.tomorrow_color,
  };
}

/** «Desde mañana a las 08:00 · hasta mañana a las 23:59» or «Hasta hoy a las 23:59». */
export function alertWindow(alert, now = Date.now()) {
  const onset = parseTimestamp(alert?.onset, now);
  const expires = parseTimestamp(alert?.expires, now);
  const parts = [];
  if (onset != null && onset > now) parts.push(`Desde ${whenLabel(onset, now)}`);
  if (expires != null) parts.push(`${parts.length ? 'hasta' : 'Hasta'} ${whenLabel(expires, now)}`);
  return parts.join(' · ');
}

// ── Wind direction in words ──────────────────────────────────────────────────────────────────
// «13 km/h del noroeste» instead of «13 km/h NO»: the abbreviations are unclear to many readers
// (and «NO» reads as "no"). The backend sends Spanish 16-point codes (O = oeste); English letters
// and «C» (calma) are accepted too. The in-between points use the hyphenated forms Spanish
// weather reports use («oeste-noroeste»).
const WIND_NAMES = {
  N: 'norte', NNE: 'nor-noreste', NE: 'noreste', ENE: 'este-noreste',
  E: 'este', ESE: 'este-sureste', SE: 'sureste', SSE: 'sur-sureste',
  S: 'sur', SSO: 'sur-suroeste', SO: 'suroeste', OSO: 'oeste-suroeste',
  O: 'oeste', ONO: 'oeste-noroeste', NO: 'noroeste', NNO: 'nor-noroeste',
};
const EN_TO_ES = { W: 'O', NW: 'NO', SW: 'SO', WNW: 'ONO', WSW: 'OSO', NNW: 'NNO', SSW: 'SSO' };

function windKey(code) {
  const c = String(code ?? '').trim().toUpperCase();
  if (!c) return null;
  if (c === 'C' || c === 'CALMA') return 'C';
  const k = EN_TO_ES[c] || c;
  return WIND_NAMES[k] ? k : null;
}

/** «Noroeste» — the direction on its own, e.g. under a «Dirección» label. '' when unknown. */
export function windDirName(code) {
  const k = windKey(code);
  if (!k) return '';
  const name = k === 'C' ? 'calma' : WIND_NAMES[k];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** «del noroeste» — where the wind comes from, to follow a speed. «en calma» for C; '' when unknown. */
export function windFrom(code) {
  const k = windKey(code);
  if (!k) return '';
  return k === 'C' ? 'en calma' : `del ${WIND_NAMES[k]}`;
}

/** AEMET's wind terms for a mean speed in km/h (its forecast terminology: flojo 6–20, moderado
 *  21–40, fuerte 41–70, muy fuerte 71–120, huracanado above). The page labelled its own bands
 *  (15/25/40) «AEMET». Below 6 km/h is calm or near it: «Flojo» too, as AEMET's forecasts say. */
export function windBand(kmh) {
  const v = Number(kmh) || 0;
  if (v > 120) return { label: 'Huracanado', color: '#7F1D1D', bar: 'bg-red-700' };
  if (v >= 71) return { label: 'Muy fuerte', color: '#EF4444', bar: 'bg-red-400' };
  if (v >= 41) return { label: 'Fuerte', color: '#F97316', bar: 'bg-orange-400' };
  if (v >= 21) return { label: 'Moderado', color: '#F59E0B', bar: 'bg-amber-400' };
  return { label: 'Flojo', color: '#94A3B8', bar: 'bg-slate-300' };
}
